/**
 * Terms acceptance (OCP-525) - the form that records the account's decision
 * on the CURRENT Terms version, and the Settings tab that shows the state.
 *
 * Used in two places with ONE wording:
 *   - the order review dialog (OCP-526), as a step when the account has not
 *     accepted the current version (a fresh version, or an account from
 *     before the registry);
 *   - Settings -> Terms, where a customer re-reads, accepts a new version
 *     after a change notice, or records an objection.
 *
 * The statement the checkbox confirms is SERVED by the API
 * (`statement` on `/v1/portal/settings/terms`) so the text on screen and the
 * text stored on the acceptance row cannot drift. The full Terms text lives
 * at /terms, from the same registry row.
 *
 * OCP-847 - the AVV (data processing agreement, Art. 28 GDPR) is mandatory
 * next to the AGB. While an AVV is in force that the account has not
 * accepted, the AGB form shows it next to the AGB (both rows land in one
 * transaction); an AVV decided on its own (an AVV change, or an account that
 * accepted the AGB before the AVV was published) uses the same form with
 * `kind="avv"`.
 *
 * OCP-856 - the AVV is a separate agreement: it gets its OWN mandatory
 * checkbox with its own served statement (`avv_statement`) and a link to
 * exactly the version shown. The submit stays disabled until every open
 * document is ticked, and the request carries `accept_avv: true` only when
 * the AVV box is ticked - the server refuses an AGB acceptance without it.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n, { currentLocale } from '../i18n';

import { api, ApiError } from '../lib/api';

export type TermsKind = 'agb' | 'avv';

export interface TermsVersionSummary {
  id: string;
  /** OCP-847 */
  kind?: TermsKind;
  version: string;
  title: string;
  language: string;
  content_sha256: string;
  change_summary: string | null;
  effective_from: string;
  objection_deadline: string | null;
  published_at: string;
  current: boolean;
}

/** OCP-847 - one document's state (the AVV next to the AGB). */
export interface TermsKindStatus {
  kind: TermsKind;
  state: 'accepted' | 'pending' | 'rejected' | 'none' | 'unpublished';
  current_version: TermsVersionSummary | null;
  accepted_version: TermsVersionSummary | null;
  accepted_at: string | null;
  notice: { sent_at: string; mail_id: string | null; mail_error: string | null } | null;
  announced_version?: TermsVersionSummary | null;
  announced_decision?: 'accepted' | 'rejected' | null;
  termination?: { scheduled_at: string; ends_at: string | null; subscription_ids: string[]; reactivated_at: string | null } | null;
}

export interface TermsStatusResponse {
  state: 'accepted' | 'pending' | 'rejected' | 'none' | 'unpublished';
  statement: string;
  current_version: TermsVersionSummary | null;
  accepted_version: TermsVersionSummary | null;
  accepted_at: string | null;
  /** OCP-657 */
  announced_version?: TermsVersionSummary | null;
  announced_decision?: 'accepted' | 'rejected' | null;
  termination?: { scheduled_at: string; ends_at: string | null; subscription_ids: string[]; reactivated_at: string | null } | null;
  notice: { sent_at: string; mail_id: string | null; mail_error: string | null } | null;
  can_conclude: boolean;
  /** OCP-847 - the AVV; absent on an API from before OCP-847. */
  avv?: TermsKindStatus;
  avv_statement?: string;
  /**
   * OCP-856 - the language of `statement` / `avv_statement` (the screen's language). Sent back with
   * the decision so the row stores exactly the sentence shown; absent on an API from before OCP-856.
   */
  statement_language?: 'de' | 'en';
  role: 'owner' | 'admin' | 'viewer' | 'billing-only';
}

/**
 * OCP-847 - the AVV that has to be accepted next to the AGB (OCP-856: its own checkbox): in force
 * and not accepted by this account. Null when there is none (none published,
 * or already accepted).
 */
export function pendingAvv(status: TermsStatusResponse | null | undefined): TermsVersionSummary | null {
  const avv = status?.avv;
  if (!avv || avv.state === 'accepted' || avv.state === 'unpublished') return null;
  return avv.current_version;
}

/** OCP-847 - the public page for exactly this version of a document. */
export function termsTextHref(version: Pick<TermsVersionSummary, 'kind' | 'version'>): string {
  const params = new URLSearchParams();
  if (version.kind === 'avv') params.set('kind', 'avv');
  params.set('version', version.version);
  return `/terms?${params.toString()}`;
}

export const TERMS_STATUS_PATH = '/v1/portal/settings/terms';

/** OCP-856 - the status read in the portal's language: the statements come back in the language shown. */
export function termsStatusUrl(): string {
  return `${TERMS_STATUS_PATH}?lang=${encodeURIComponent(currentLocale())}`;
}
export const TERMS_DECIDE_PATH = '/v1/portal/settings/terms/decide';

/**
 * Offline fallback for the statement, used only when the status read failed.
 * >>> Keep byte-identical to `TERMS_ACCEPTANCE_STATEMENT` in
 * >>> `apps/api/src/services/terms.ts` - the server stores its own copy.
 * OCP-856 - the localized fallbacks (`fallbackStatement` / `fallbackAvvStatement` in the
 * terms-acceptance locale files) equal `TERMS_ACCEPTANCE_STATEMENTS` / `AVV_ACCEPTANCE_STATEMENTS`
 * of their language, so a fallback shown in `de` is the sentence the server stores for `de`.
 */
export const FALLBACK_TERMS_STATEMENT =
  'I have read the orboto Terms in the version shown and accept them on behalf of my company.';

/**
 * OCP-847 - offline fallback for the AVV statement.
 * >>> Keep byte-identical to `AVV_ACCEPTANCE_STATEMENT` in `apps/api/src/services/terms.ts`.
 */
export const FALLBACK_AVV_STATEMENT =
  'I have read the orboto data processing agreement (DPA, Art. 28 GDPR) in the version shown and accept it ' +
  'on behalf of my company.';

/** The fallback statement in the active portal language (OCP-656); the English value stays the server constant. */
export function fallbackTermsStatement(): string {
  return i18n.t('terms-acceptance:fallbackStatement', { defaultValue: FALLBACK_TERMS_STATEMENT });
}

export function formatTermsDate(iso: string): string {
  return iso.slice(0, 10);
}

export function canDecideTerms(role: TermsStatusResponse['role']): boolean {
  return role === 'owner' || role === 'admin';
}

export function TermsAcceptanceForm({
  initial,
  context,
  submitLabel,
  cancelLabel,
  onCancel,
  onSaved,
  allowReject = false,
  kind = 'agb',
  versionOverride,
}: {
  /** OCP-847 - the document decided on: the AGB (with the AVV when one is pending) or the AVV on its own. */
  kind?: TermsKind;
  /** The version to decide on instead of the current one (an announced change). */
  versionOverride?: TermsVersionSummary | null;
  /** Pre-fetched state, when the host already has it (the order dialog does). */
  initial?: TermsStatusResponse | null;
  /** Where the decision is made - stored on the acceptance row. */
  context: 'checkout' | 'free_plan' | 'code_redeem' | 'license_activation' | 're_acceptance';
  submitLabel: string;
  cancelLabel?: string;
  onCancel?: () => void;
  onSaved: (r: TermsStatusResponse) => void;
  /** Settings only: offer the objection path next to the acceptance. */
  allowReject?: boolean;
}) {
  const { t } = useTranslation('terms-acceptance');
  const [state, setState] = useState<TermsStatusResponse | null>(initial ?? null);
  const [checked, setChecked] = useState(false);
  // OCP-856 - the AVV's own checkbox (only rendered while an AVV is open next to the AGB)
  const [avvChecked, setAvvChecked] = useState(false);
  const [saving, setSaving] = useState<'accept' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) return;
    const ac = new AbortController();
    void (async () => {
      try {
        setState(await api<TermsStatusResponse>(termsStatusUrl(), { signal: ac.signal }));
      } catch {
        // Fall back to the constant; the server stores its own wording.
      }
    })();
    return () => ac.abort();
  }, [initial]);

  const version = versionOverride ?? (kind === 'avv' ? (state?.avv?.current_version ?? null) : (state?.current_version ?? null));
  // OCP-847 - the AVV open next to the AGB (none when not published or already accepted); OCP-856 - its own checkbox.
  const companion = kind === 'agb' ? pendingAvv(state) : null;
  const statement =
    kind === 'avv'
      ? (state?.avv_statement ?? t('fallbackAvvStatement', { defaultValue: FALLBACK_AVV_STATEMENT }))
      : (state?.statement ?? fallbackTermsStatement());
  const avvStatement = state?.avv_statement ?? t('fallbackAvvStatement', { defaultValue: FALLBACK_AVV_STATEMENT });
  const readOnly = state ? !canDecideTerms(state.role) : false;
  // OCP-856 - every open document needs its own tick before the acceptance can be sent
  const allTicked = checked && (!companion || avvChecked);

  const decide = useCallback(
    async (decision: 'accepted' | 'rejected') => {
      if (!version || saving) return;
      if (decision === 'accepted' && !allTicked) return;
      setSaving(decision === 'accepted' ? 'accept' : 'reject');
      setError(null);
      try {
        const r = await api<TermsStatusResponse>(TERMS_DECIDE_PATH, {
          method: 'POST',
          body: {
            version_id: version.id,
            decision,
            context,
            // OCP-856 - the AVV acceptance is explicit: sent only when its own box is ticked
            ...(companion && decision === 'accepted' && avvChecked ? { avv_version_id: companion.id, accept_avv: true } : {}),
            // OCP-856 - the language of the statements on screen, so the rows store exactly what was shown
            statement_language: state?.statement_language ?? currentLocale(),
          },
        });
        setState(r);
        onSaved(r);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError(t('form.errorRole'));
        } else if (err instanceof ApiError && err.code === 'version_not_found') {
          setError(t('form.errorVersion'));
        } else if (err instanceof ApiError && err.code === 'terms_not_published') {
          setError(t('form.errorUnpublished'));
        } else if (err instanceof ApiError && err.code === 'avv_not_published') {
          setError(t('form.errorAvvUnpublished'));
        } else if (err instanceof ApiError && (err.code === 'avv_acceptance_required' || err.code === 'avv_version_invalid')) {
          setError(t('form.errorAvv'));
        } else {
          setError(t('form.errorGeneric'));
        }
      } finally {
        setSaving(null);
      }
    },
    [version, saving, context, onSaved, t, companion, allTicked, avvChecked, state],
  );

  if (state && kind === 'agb' && state.state === 'unpublished') {
    return (
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400" data-testid="terms-unpublished">
        {t('form.unpublished')}
      </p>
    );
  }

  return (
    <div className="mt-3" data-testid="terms-acceptance-form">
      {version && (
        <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-sm">
          <div className="font-medium text-gray-900 dark:text-gray-100" data-testid="terms-version-label">
            {t('form.versionLabel', { title: version.title, version: version.version })}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {t('form.effectiveFrom', { date: formatTermsDate(version.effective_from) })}
            {version.objection_deadline ? t('form.objectionUntil', { date: formatTermsDate(version.objection_deadline) }) : ''}
          </div>
          {version.change_summary && (
            <p className="mt-2 whitespace-pre-line text-xs text-gray-600 dark:text-gray-400" data-testid="terms-change-summary">
              {version.change_summary}
            </p>
          )}
          {/* A plain anchor on purpose: it opens a new tab, and the form must
              render outside a router (the order dialog is router-agnostic). */}
          <a href={termsTextHref({ kind, version: version.version })} target="_blank" rel="noopener" className="mt-2 inline-block text-xs text-brand-500 underline">
            {t('form.readFullText')}
          </a>
        </div>
      )}
      {!readOnly && (
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={saving !== null || !version}
            data-testid="terms-accept-checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
          />
          <span className="text-gray-700 dark:text-gray-300">{statement}</span>
        </label>
      )}

      {/* OCP-856 - the AVV is a separate agreement: its own box, its own statement, its own link. */}
      {companion && (
        <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-sm" data-testid="terms-avv-companion">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {t('form.versionLabel', { title: companion.title, version: companion.version })}
          </div>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{t('form.avvSeparate')}</p>
          <a href={termsTextHref({ kind: 'avv', version: companion.version })} target="_blank" rel="noopener" className="mt-2 inline-block text-xs text-brand-500 underline" data-testid="terms-avv-companion-link">
            {t('form.readFullText')}
          </a>
        </div>
      )}
      {companion && !readOnly && (
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={avvChecked}
            onChange={(e) => setAvvChecked(e.target.checked)}
            disabled={saving !== null || !version}
            data-testid="terms-avv-accept-checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700"
          />
          <span className="text-gray-700 dark:text-gray-300">{avvStatement}</span>
        </label>
      )}

      {readOnly && (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400" data-testid="terms-read-only">
          {t('form.readOnly')}
        </p>
      )}

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!readOnly && (
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving !== null}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              {cancelLabel ?? t('form.cancel')}
            </button>
          )}
          {allowReject && (
            <button
              type="button"
              onClick={() => void decide('rejected')}
              disabled={saving !== null || !version}
              data-testid="terms-reject-button"
              className="rounded border border-amber-300 dark:border-amber-800 px-3 py-1.5 text-sm text-amber-900 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50"
            >
              {saving === 'reject' ? t('form.recording') : t('form.object')}
            </button>
          )}
          <button
            type="button"
            onClick={() => void decide('accepted')}
            disabled={!allTicked || saving !== null || !version}
            data-testid="terms-accept-button"
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {saving === 'accept' ? t('form.saving') : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/** Settings -> Terms: state, history pointer, and the form when a decision is due. */
export function TermsSettingsTab() {
  const { t } = useTranslation('terms-acceptance');
  const [status, setStatus] = useState<TermsStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setStatus(await api<TermsStatusResponse>(termsStatusUrl()));
    } catch {
      setError(t('settings.loadError'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section data-testid="terms-settings-tab">
      <h2 className="text-base font-semibold">{t('settings.heading')}</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        {t('settings.intro')}
        <a href="/terms" target="_blank" rel="noopener" className="text-brand-500 underline">
          {t('settings.introLink')}
        </a>
        .
      </p>
      {/* OCP-851 - legal links: the service price list AGB 6.6 refers to (public, versioned). */}
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-testid="terms-settings-service-prices">
        {t('settings.servicePricesIntro')}
        <a href="/service-prices" target="_blank" rel="noopener" className="text-brand-500 underline">
          {t('settings.servicePricesLink')}
        </a>
        .
      </p>

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {status && (
        <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 p-3 text-sm" data-testid="terms-state">
          {status.state === 'accepted' && status.accepted_version && (
            <p>
              <span className="font-medium text-green-700 dark:text-green-300">{t('settings.accepted')}</span>
              {t('settings.acceptedLine', { version: status.accepted_version.version, date: status.accepted_at ? formatTermsDate(status.accepted_at) : '-' })}
            </p>
          )}
          {status.state === 'pending' && (
            <p>
              <span className="font-medium text-amber-700 dark:text-amber-300">{t('settings.pending')}</span>
              {t('settings.pendingLine', { version: status.current_version?.version })}
              {status.accepted_version ? t('settings.pendingLast', { version: status.accepted_version.version }) : ''}.
              {status.notice ? t('settings.pendingNotice', { date: status.notice.sent_at.slice(0, 10) }) : ''}
            </p>
          )}
          {status.state === 'rejected' && (
            <p>
              <span className="font-medium text-red-700 dark:text-red-300">{t('settings.rejected')}</span>
              {t('settings.rejectedLine', { version: status.current_version?.version })}
            </p>
          )}
          {status.state === 'none' && (
            <p>
              <span className="font-medium text-amber-700 dark:text-amber-300">{t('settings.none')}</span>
              {t('settings.noneLine')}
            </p>
          )}
          {status.state === 'unpublished' && <p>{t('settings.unpublished')}</p>}
          {status.announced_version && (
            <p className="mt-2" data-testid="terms-announced">
              <span className="font-medium text-amber-700 dark:text-amber-300">{t('settings.announced')}</span>
              {t('settings.announcedLine', { version: status.announced_version.version, date: formatTermsDate(status.announced_version.effective_from) })}
              {status.announced_decision === 'accepted' ? t('settings.announcedAccepted') : status.announced_decision === 'rejected' ? t('settings.announcedRejected') : ''}
            </p>
          )}
          {status.termination && !status.termination.reactivated_at && (
            <p className="mt-2 text-red-700 dark:text-red-300" data-testid="terms-termination">
              {status.termination.ends_at ? t('settings.termination', { date: formatTermsDate(status.termination.ends_at) }) : t('settings.terminationNoDate')}
            </p>
          )}
        </div>
      )}
      {status && status.announced_version && status.announced_decision !== 'accepted' && (
        <TermsAcceptanceForm
          initial={status}
          versionOverride={status.announced_version}
          context="re_acceptance"
          submitLabel={t('settings.accept')}
          allowReject
          onSaved={(r) => setStatus(r)}
        />
      )}

      {status && status.state !== 'accepted' && status.state !== 'unpublished' && (
        <TermsAcceptanceForm
          initial={status}
          context="re_acceptance"
          submitLabel={t('settings.accept')}
          allowReject
          onSaved={(r) => setStatus(r)}
        />
      )}

      {/* OCP-847 / OCP-856 - the AVV (Art. 28 GDPR): a separate, mandatory agreement next to the AGB. */}
      {status?.avv && <AvvSettingsBlock status={status} onSaved={(r) => setStatus(r)} />}
    </section>
  );
}

/**
 * OCP-847 - Settings: the AVV state next to the AGB, and the AVV form when the
 * AVV is decided on its own (the AGB is accepted, the AVV is not, or an AVV
 * change is announced). While the AGB itself is open, the AGB form above
 * carries the AVV (OCP-856: with its own checkbox) and this block only shows the state.
 */
function AvvSettingsBlock({ status, onSaved }: { status: TermsStatusResponse; onSaved: (r: TermsStatusResponse) => void }) {
  const { t } = useTranslation('terms-acceptance');
  const avv = status.avv!;
  const agbOpen = status.state !== 'accepted' && status.state !== 'unpublished';
  return (
    <div className="mt-6" data-testid="terms-avv-settings">
      <h3 className="text-sm font-semibold">{t('settings.avvHeading')}</h3>
      <div className="mt-2 rounded border border-gray-200 dark:border-gray-800 p-3 text-sm" data-testid="terms-avv-state">
        {avv.state === 'unpublished' && <p>{t('settings.avvUnpublished')}</p>}
        {avv.state === 'accepted' && avv.accepted_version && (
          <p>
            <span className="font-medium text-green-700 dark:text-green-300">{t('settings.accepted')}</span>
            {t('settings.acceptedLine', { version: avv.accepted_version.version, date: avv.accepted_at ? formatTermsDate(avv.accepted_at) : '-' })}{' '}
            <a href={termsTextHref({ kind: 'avv', version: avv.accepted_version.version })} target="_blank" rel="noopener" className="text-brand-500 underline">
              {t('settings.avvRead')}
            </a>
          </p>
        )}
        {(avv.state === 'pending' || avv.state === 'none') && (
          <p>
            <span className="font-medium text-amber-700 dark:text-amber-300">{t('settings.pending')}</span>
            {t('settings.pendingLine', { version: avv.current_version?.version })}
            {avv.accepted_version ? t('settings.pendingLast', { version: avv.accepted_version.version }) : ''}.
            {avv.notice ? t('settings.pendingNotice', { date: avv.notice.sent_at.slice(0, 10) }) : ''}
          </p>
        )}
        {avv.state === 'rejected' && (
          <p>
            <span className="font-medium text-red-700 dark:text-red-300">{t('settings.rejected')}</span>
            {t('settings.rejectedLine', { version: avv.current_version?.version })}
          </p>
        )}
        {avv.announced_version && (
          <p className="mt-2" data-testid="terms-avv-announced">
            <span className="font-medium text-amber-700 dark:text-amber-300">{t('settings.announced')}</span>
            {t('settings.announcedLine', { version: avv.announced_version.version, date: formatTermsDate(avv.announced_version.effective_from) })}
            {avv.announced_decision === 'accepted' ? t('settings.announcedAccepted') : avv.announced_decision === 'rejected' ? t('settings.announcedRejected') : ''}
          </p>
        )}
        {avv.termination && !avv.termination.reactivated_at && (
          <p className="mt-2 text-red-700 dark:text-red-300">
            {avv.termination.ends_at ? t('settings.avvTermination', { date: formatTermsDate(avv.termination.ends_at) }) : t('settings.avvTerminationNoDate')}
          </p>
        )}
      </div>
      {avv.announced_version && avv.announced_decision !== 'accepted' && (
        <TermsAcceptanceForm
          initial={status}
          kind="avv"
          versionOverride={avv.announced_version}
          context="re_acceptance"
          submitLabel={t('settings.avvAccept')}
          allowReject
          onSaved={onSaved}
        />
      )}
      {!agbOpen && avv.state !== 'accepted' && avv.state !== 'unpublished' && (
        <TermsAcceptanceForm initial={status} kind="avv" context="re_acceptance" submitLabel={t('settings.avvAccept')} allowReject onSaved={onSaved} />
      )}
    </div>
  );
}
